import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

import { requestHandler } from "@/utils/apiUtils";
import SupabasePool from "@/lib/supabaseClient";
import { HtmlContentNormalizer } from "@/utils/HtmlContentNormalizer";
import { DateUtils } from "@/utils/dateUtil";
import imgUrlMapper from "@/utils/imgUrl-mapping";

import NotfoundPage from "@/components/error/NotfoundPage";
import StackIconMapper from "@/components/shared/stack-iconmapper";
import { ProjectDetailSkeleton } from "./project-detail-skeleton";
import {
  EditorProvider,
  SimpleEditorContents,
  useSimpleEditor,
} from "@squirrel309/my-testcounter";

import { type STACK_TYPES } from "@/type/ProjectTypes";
import { useAuthentication } from "@/hooks/useAuth";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { IconLeft } from "react-day-picker";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type DetailProps = {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  img_key: string;
  start_date: string;
  end_date: string;
  project_member: string;
  project_url: string;
  company: string;
  project_contents: Array<{ id: number; contents: string }>;
  project_meta_stack: Array<{
    project_stack: { id: number; type: STACK_TYPES; stack: string };
  }>;
  project_surmmry: Array<{ id: number; title: string; contents: string }>;
};

// ─────────────────────────────────────────────────────────────
// Utils
// ─────────────────────────────────────────────────────────────

const groupStacksByType = (stackArr: DetailProps["project_meta_stack"]) => {
  const grouped: Record<string, string[]> = {};

  stackArr.forEach(({ project_stack }) => {
    const { type, stack } = project_stack;
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(stack);
  });

  return grouped;
};

// ─────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────

const useProjectDetail = (workId: string) => {
  return useQuery({
    queryKey: [`PROJECT_DETAIL:${workId}`],
    queryFn: async () =>
      await requestHandler(async () => {
        const response = await SupabasePool.getInstance()
          .from("project_meta")
          .select(
            `
            *,
            project_contents(*),
            project_meta_stack(project_stack(id, stack, type)),
            project_surmmry(title, contents)
          `
          )
          .eq("id", workId);

        if (response.error || !response.data?.length) {
          throw new Error(
            `요청 실패: ${response.error?.message ?? "데이터 없음"}`
          );
        }

        return response;
      }),
    staleTime: Infinity,
  });
};

const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await SupabasePool.getInstance().auth.getSession();
      if (!data.session) throw new Error("권한이 없습니다.");

      const { error } = await SupabasePool.getInstance()
        .from("project_meta")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      toast.success("삭제되었습니다.");
      await queryClient.invalidateQueries({ queryKey: ["project-list"] });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────────────────────

const AdminActions = ({
  workId,
  onDelete,
}: {
  workId: string;
  onDelete: () => void;
}) => {
  const nav = useNavigate();

  return (
    <div className="flex gap-2 ml-auto">
      <button
        onClick={() => nav(`/work/write?edit=${workId}`)}
        className="text-xs bg-zinc-700 px-3 py-1 rounded hover:bg-zinc-600 transition-colors"
      >
        수정
      </button>
      <button
        onClick={onDelete}
        className="text-xs bg-zinc-700 px-3 py-1 rounded hover:bg-zinc-600 transition-colors"
      >
        삭제
      </button>
    </div>
  );
};

const ProjectDescription = ({ description }: { description: string }) => (
  <div className="flex flex-col gap-3 mt-5">
    <p className="text-sm text-zinc-400 mt-3">프로젝트 설명</p>
    <p className="text-sm text-zinc-300 leading-relaxed mb-10 max-w-[750px] break-keep">
      {description}
    </p>
  </div>
);

const ProjectMeta = ({
  startDate,
  endDate,
  member,
  url,
}: {
  startDate: string;
  endDate: string;
  member: string;
  url: string;
}) => {
  const duration = DateUtils.getDurationDays(startDate, endDate);

  return (
    <article className="flex flex-col gap-6 my-5">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-zinc-400">작업기간 & 유지보수</p>
        <p className="text-sm text-zinc-100">{duration}일</p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm text-zinc-400">투입인원</p>
        <span className="text-sm text-zinc-100">{member}</span>
      </div>

      <div className="flex flex-col gap-2 items-start">
        <p className="text-sm text-zinc-400 flex gap-2 items-center">
          Deploy URL
        </p>
        <button
          onClick={() => window.open(url, "_blank")}
          className="flex items-center gap-2 text-pink-300 hover:text-pink-400 "
        >
          <Link2 size={16} className="rotate-135" />
          <span className="line-clamp-1 max-w-[400px] text-sm  underline ">
            링크 바로가기
          </span>
        </button>
      </div>
    </article>
  );
};

const ProjectThumbnail = ({ thumbnail }: { thumbnail: string }) => (
  <div className="flex flex-col">
    <div
      className="relative aspect-5/3 rounded-2xl bg-cover bg-center  "
      style={{
        backgroundImage: `url(${thumbnail ? imgUrlMapper({ thumbnail }) : ""})`,
      }}
    ></div>{" "}
    <p className="bottom-5 ml-auto text-xs text-zinc-300 mt-2">
      Mock up pictrue
    </p>
  </div>
);

const TechStack = ({
  stacks,
}: {
  stacks: DetailProps["project_meta_stack"];
}) => {
  const grouped = groupStacksByType(stacks);
  const allStacks = Object.values(grouped).flat();

  return (
    <article className="space-y-6  pb-15">
      <h3 className="text-sm text-zinc-400">사용 기술</h3>
      <div className="flex flex-wrap gap-2 mt-2">
        <div className={cn(" gap-2 w-full")}>
          <div className="max-w-[750px] flex flex-wrap gap-2">
            {allStacks.map((e, idx) => {
              return (
                <Tooltip key={idx}>
                  <TooltipTrigger asChild>
                    <div className="flex items-center p-2 bg-zinc-800/30 gap-3 group rounded-lg  hover:bg-zinc-50/10 transition-colors">
                      <StackIconMapper
                        stackName={e}
                        className="size-5 grayscale-100 group-hover:grayscale-0 text-zinc-300 hover:text-zinc-100 transition-colors"
                      />
                      <span className="text-xs text-zinc-400 group-hover:text-zinc-50">
                        {e}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-indigo-500/90 text-zinc-50 border border-zinc-700 shadow-md text-xs">
                    <span className="font-semibold">{e}</span>
                    <br />
                    {/* {description} */}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>
    </article>
  );
};

const ProjectContent = ({ contents }: { contents: string }) => {
  const { editor } = useSimpleEditor({ editable: false });

  if (!contents || contents.length <= 10) return null;

  return (
    <section className="">
      <div className="prose dark:prose-invert prose-sm max-w-none">
        <EditorProvider editor={editor}>
          <SimpleEditorContents
            value={HtmlContentNormalizer.setImgUrl(contents)}
          />
        </EditorProvider>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

const ProjectDetail = () => {
  const nav = useNavigate();
  const auth = useAuthentication();
  const { id: workId } = useParams();

  const { data, isLoading, isError } = useProjectDetail(workId!);
  const { mutate: deleteProject } = useDeleteProject();

  // ─── Loading State ───
  if (isLoading) {
    return (
      <div className="relative min-h-screen grid-layout pt-40">
        <ProjectDetailSkeleton />
      </div>
    );
  }

  // ─── Error State ───
  if (isError || !data || !workId) {
    return <NotfoundPage redirectPath="/work" />;
  }

  // ─── Data ───
  const project = data[0] as DetailProps;

  // ─── Handlers ───
  const handleDelete = () => {
    if (confirm("삭제하시겠습니까?")) {
      deleteProject(+workId);
      nav("/work");
    }
  };

  // ─── Render ───
  return (
    <section className="pt-30 grid-layout  flex-col  md:mt-auto pb-10 animate-opacity grid lg:grid-cols-[1fr_3fr] lg:gap-30">
      <div>
        <div className=" lg:sticky top-30">
          <button
            className="flex items-center text-sm gap-2 hover:text-indigo-300"
            onClick={() => nav("/work")}
          >
            <IconLeft className="w-3" />
            뒤로가기{" "}
          </button>{" "}
          <h1 className="text-3xl md:text-3xl font-semibold text-white tracking-tight leading-relaxed  pt-15 break-keep">
            {project.title}
          </h1>{" "}
          <ProjectDescription description={project.description} />
          <ProjectMeta
            startDate={project.start_date}
            endDate={project.end_date}
            member={project.project_member}
            url={project.project_url}
          />
        </div>
      </div>
      <div className="pt-5 lg:pt-20">
        {/* Admin Actions */}
        {auth && (
          <div className="flex items-center">
            <AdminActions workId={workId} onDelete={handleDelete} />
          </div>
        )}

        {/* Project Detail */}
        <div className="flex flex-col gap-6 md:gap-12 mt-3">
          <ProjectThumbnail thumbnail={project.thumbnail} />

          <TechStack stacks={project.project_meta_stack} />
        </div>

        {/* Project Summary */}
        {/* {project.project_surmmry.length > 0 && (
        <ProjectSummary summaries={project.project_surmmry} />
      )} */}

        {/* Project Content */}
        <ProjectContent contents={project.project_contents[0]?.contents} />
      </div>
    </section>
  );
};

export default ProjectDetail;
